import React from "react";
import { motion } from "framer-motion";
import { Github, Linkedin, Mail, FileText, ChevronUp } from "lucide-react";
import resume from "../assets/resume.pdf";
import "../assets/fonts/fonts.css";

const defaultSocials = [
  { name: "GitHub", href: "https://github.com/SamZhang150", icon: Github },
  { name: "LinkedIn", href: "https://www.linkedin.com/in/moses-zhang-81084b2aa/", icon: Linkedin },
  { name: "Email", href: "mailto:mzhang.labor@gmail.com", icon: Mail },
  { name: "Resume", href: resume, icon: FileText },
];

export default function Footer({
  name = "Moses Zhang",
  socialLinks = defaultSocials,
  frosted = true,
}) {
  const year = new Date().getFullYear();

  return (
    <footer className={`relative mt-20 text-neutral-200 ${frosted ? 'border-t border-white/15 bg-neutral-900/40 backdrop-blur-xl supports-[backdrop-filter]:bg-neutral-900/40 backdrop-saturate-150' : 'border-t border-slate-800/80 bg-neutral-950'}`}>
      {/* subtle top shine */}
      <div
        className="absolute inset-x-0 -top-px h-px bg-gradient-to-r from-transparent via-neutral-600 to-transparent"
        aria-hidden
      />

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        {/* Top row: brand + nav */}
        <div className="flex flex-col items-center gap-6 md:flex-row md:items-start md:justify-between">
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.4 }}
            className="text-center md:text-left"
          >
            <p className="text-lg nsans-7-sc-me leading-none">{name}</p>
            <p className="mt-1 max-w-md text-sm text-gray-400 nsans-7-sc-li">
              Software Engineer • building things.
            </p>
          </motion.div>
        </div>

        {/* Middle row: socials + back-to-top */}
        <div className="mt-8 flex flex-col items-center justify-between gap-6 border-t border-white/10 pt-6 sm:flex-row">
          <ul className="flex flex-wrap justify-center gap-3 sm:justify-start">
            {socialLinks.map(({ name: label, href, icon: Icon }, i) => (
              <motion.li
                key={label}
                initial={{ opacity: 0, y: 4 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.04, duration: 0.25 }}
              >
                <a
                  href={href}
                  aria-label={label}
                  target="_blank"
                  rel="noreferrer"
                  className="group inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-2 ring-1 ring-inset ring-white/10 transition hover:bg-white/10 hover:ring-white/20"
                >
                  <Icon
                    className="h-4 w-4 transition-transform group-hover:-translate-y-0.5"
                    aria-hidden
                  />
                  <span className="text-sm nsans-7-sc-li">{label}</span>
                </a>
              </motion.li>
            ))}
          </ul>

          <motion.button
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="inline-flex items-center gap-2 rounded-full px-3 py-2 text-sm nsans-7-sc-li ring-1 ring-inset ring-white/10 transition hover:bg-white/10 hover:ring-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
            whileTap={{ scale: 0.97 }}
            aria-label="Scroll to top"
          >
            <ChevronUp className="h-4 w-4" aria-hidden />
            Back to top
          </motion.button>
        </div>

        {/* Copyright */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-6 text-center nsans-7-sc-li text-xs text-gray-400"
        >
          © {year} {name}. All rights reserved.
        </motion.p>
      </div>
    </footer>
  );
}
